/**
 * AddFieldMenu — the "+" popover: the live-field palette (each lands in its
 * CANONICAL group — gate/status/… are auxiliary, notice is back — so the
 * backend's conventional-key contract holds no matter which region's "+" was
 * clicked) plus a plain custom field that lands in the clicked region.
 */
import type { PassDesign } from '@insurwiz/wallet-core';

import { useT } from '../../i18n';
import { addField, setGroup, type FieldGroup } from '../../lib/fieldEditing';

import { Popover } from './Popover';
import { liveFieldsAvailable } from './liveFieldPalette';

export interface AddFieldMenuProps {
  design: PassDesign;
  group: FieldGroup;
  onChange: (next: PassDesign) => void;
  onClose: () => void;
  /**
   * Fired INSTEAD of onClose after a custom-field add, with the new row's
   * (group, index) address, so the host can auto-select it (open its
   * inspector — a fresh {key:'',value:''} row is near-invisible on the canvas
   * otherwise). Calling onClose too would immediately reset the selection the
   * host just made.
   */
  onAdded?: (group: FieldGroup, index: number) => void;
}

export function AddFieldMenu({
  design,
  group,
  onChange,
  onClose,
  onAdded,
}: AddFieldMenuProps): JSX.Element {
  const t = useT();
  const live = liveFieldsAvailable(design);
  return (
    <Popover label={t('addMenu.title')} onClose={onClose}>
      <div className="space-y-2">
        {live.length > 0 ? (
          <>
            <p className="text-xs font-medium text-slate-700">{t('addMenu.liveHeading')}</p>
            <div className="space-y-1">
              {live.map((spec) => (
                <button
                  key={spec.key}
                  type="button"
                  className="w-full text-left rounded-md px-2 py-1.5 ring-1 ring-slate-200 hover:ring-amber-400 hover:bg-amber-50"
                  onClick={() => {
                    // Insert a COPY — the design must never hold the module
                    // constant by reference (state and the shared palette
                    // spec must stay independent objects).
                    onChange(
                      setGroup(design, spec.group, [
                        ...(design.fields[spec.group] ?? []),
                        { ...spec.field },
                      ]),
                    );
                    onClose();
                  }}
                >
                  <span className="flex items-center gap-1.5 text-sm font-medium">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> {spec.title}
                  </span>
                  <span className="block text-[11px] text-slate-500">{spec.description}</span>
                </button>
              ))}
            </div>
            <div className="border-t border-slate-100 pt-2" />
          </>
        ) : null}
        <button
          type="button"
          className="w-full text-left rounded-md px-2 py-1.5 ring-1 ring-slate-200 hover:ring-brand-500 text-sm"
          onClick={() => {
            // The new row lands at the END of the clicked region — capture
            // its index BEFORE the add for the host's auto-select.
            const index = (design.fields[group] ?? []).length;
            onChange(addField(design, group));
            if (onAdded) {
              onAdded(group, index);
            } else {
              onClose();
            }
          }}
        >
          {t('addMenu.custom')}
          <span className="block text-[11px] text-slate-500">{t('addMenu.customHint')}</span>
        </button>
      </div>
    </Popover>
  );
}
